import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Api from '../api';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

const AppContext = createContext();

export function AppProvider({ children }) {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [ingredients, setIngredients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [api] = useState(() => new Api(process.env.REACT_APP_API_BASE_URL));

  // Actualiza el token en la instancia de Api cuando cambie
  useEffect(() => {
    api.setToken(token);
  }, [token, api]);

  // Cargar usuario/token de localStorage al iniciar
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser && storedToken) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      api.setToken(storedToken);
      setToken(storedToken);
      
      // Cargar idioma guardado del usuario
      if (userData.language) {
        setLanguage(userData.language);
        i18n.changeLanguage(userData.language);
      }
    }
    setLoading(false);
  }, [api, i18n]);

  // Función para ordenar ingredientes según el idioma actual
  function sortIngredientsByLanguage(ings, lang) {
    return [...ings].sort((a, b) => {
      const nameA = (a.translations?.[lang] || a.name || '').toLowerCase();
      const nameB = (b.translations?.[lang] || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  // Actualiza ingredientes cuando cambia el idioma
  const refreshIngredients = useCallback(() => {
    api.getIngredients({ lang: language })
      .then(data => {
        setIngredients(sortIngredientsByLanguage(data, language));
      })
      .catch(() => setIngredients([]));
  }, [api, language]);

  // Reordena ingredientes cuando cambia el idioma
  useEffect(() => {
    setIngredients(ings => sortIngredientsByLanguage(ings, language));
  }, [language]);

  const refreshCategories = useCallback(() => {
    api.getCategories()
      .then(data => setCategories(data))
      .catch(() => setCategories([]));
  }, [api]);

  useEffect(() => {
    if (loading) return;
    refreshIngredients();
    refreshCategories();
  }, [loading, token, refreshIngredients, refreshCategories]);


  // Fetch categories once
  useEffect(() => {
    refreshCategories();
  }, [refreshCategories]);

  const changeLanguage = async (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    
    // Guardar preferencia de idioma en backend si el usuario está autenticado
    if (user && token) {
      try {
        await api.updateUserLanguage(lang);
        // Actualizar el usuario en localStorage con el nuevo idioma
        const updatedUser = { ...user, language: lang };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        console.error('Error al guardar preferencia de idioma:', error);
      }
    }
  };

  const login = (userObj, tokenStr) => {
    api.setToken(tokenStr);
    setUser(userObj);
    setToken(tokenStr);
    localStorage.setItem('user', JSON.stringify(userObj));
    localStorage.setItem('token', tokenStr);
    
    // Cargar idioma del usuario
    if (userObj.language) {
      changeLanguage(userObj.language);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setToken(null);
      setSessionExpired(true);
    };
    
    const onTokenRefreshed = (event) => {
      const { newToken } = event.detail;
      setToken(newToken);
      // Actualiza también localStorage por si acaso
      localStorage.setItem('token', newToken);
    };
    
    window.addEventListener('session-expired', onSessionExpired);
    window.addEventListener('token-refreshed', onTokenRefreshed);
    
    return () => {
      window.removeEventListener('session-expired', onSessionExpired);
      window.removeEventListener('token-refreshed', onTokenRefreshed);
    };
  }, []);

  const handleRelogin = () => {
    setSessionExpired(false);
  };

  return (
    <AppContext.Provider
     value={{ language, changeLanguage, ingredients, user, token, login, logout, refreshIngredients, loading, api, categories }}>
      {children}
      <Dialog open={sessionExpired} disableEscapeKeyDown>
        <DialogTitle>Sesión expirada</DialogTitle>
        <DialogContent>
          Tu sesión ha expirado. Por favor, vuelve a iniciar sesión para continuar usando la aplicación.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRelogin} autoFocus>
            Volver a iniciar sesión
          </Button>
        </DialogActions>
      </Dialog>
    </AppContext.Provider>
  );
}

export function useAppContext() {
  return useContext(AppContext);
}
